import './styles.scss'
import React, { useContext, useState, useEffect, useRef, useCallback, memo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Lottie from 'lottie-react'
import { useTranslation } from 'react-i18next'
import { Chart } from 'primereact/chart'
import { Toast } from 'primereact/toast'
import { Chart as ChartJS } from 'chart.js'
import { CrosshairPlugin } from 'chartjs-plugin-crosshair'
import { getLocalLoggedInStatus, getLocalTheme, getLocalUserDetails } from 'Utils/LocalStorage_Handler'
import { ROUTE_STRINGS } from 'Utils/Constants'
import { WEATHER_METHODS } from 'Utils/WeatherUtils/method'
import CustomDropdown from 'Components/CustomDropdown'
import { commonMethods } from 'Utils/commonMethods'
import CreateAccountInfoModal from 'Components/UIComponent/CreateAccountInfoModal'
import { CoffeeWebContext } from 'Context/CoffeeWebContext'
import { fetchUniqueCountriesData, fetchUniqueStatesData, fetchFortNightWeatherData, fetchHourlyWeatherData, fetchSearchResults, fetchUserSavedLocations, deleteLocationForUser, saveLocationForUser, fetchUniqueRegionsData, fetchCountryStateRegionLocations, fetchOtherLocations, fetchLocations, fetchWeatherData } from 'Services/Supabase/Weather'
import { getAccessButtonAndInfo } from 'Utils/subscriptionAccess'
import { WeatherLocationsColumn } from './Components/WeatherLocationColumn'
import { GraphSkeleton, HourlyForecastSkeleton, ForecastItemSkeleton } from './Components/Skeleton'
import { WeatherNoDataCard, OtherLocationsNoDataCard } from './Components/NoDataCard'

const HorizontalCrosshairPlugin = {
  id: 'horizontalCrosshair',
  afterEvent(chart, args) {
    const e = args?.event
    const area = chart?.chartArea

    if (!e || !area || e.type === 'mouseout') {
      // eslint-disable-next-line
      chart.$horizontalCrosshair = null

      return
    }

    const isInsideChartArea = e.x >= area.left && e.x <= area.right && e.y >= area.top && e.y <= area.bottom

    // eslint-disable-next-line
    chart.$horizontalCrosshair = isInsideChartArea ? { y: e.y } : null
  },
  afterDraw(chart, _args, options) {
    const pos = chart.$horizontalCrosshair
    const area = chart?.chartArea

    if (!pos || !area) return

    const lineOptions = options?.line || {}
    const color = lineOptions.color ?? 'rgba(255, 255, 255, 0.6)'
    const width = lineOptions.width ?? 1
    const dashPattern = lineOptions.dashPattern ?? [6, 6]

    const { ctx } = chart

    ctx.save()
    ctx.beginPath()
    ctx.setLineDash(dashPattern)
    ctx.lineWidth = width
    ctx.strokeStyle = color
    ctx.moveTo(area.left, pos.y)
    ctx.lineTo(area.right, pos.y)
    ctx.stroke()
    ctx.restore()
  }
}

ChartJS.register(CrosshairPlugin, HorizontalCrosshairPlugin)

const Weather = () => {
  const { t } = useTranslation()
  const userDetails = getLocalUserDetails()
  const {
    timeMethods: { getUtcTime, getDayOfWeek, getHourlyTime, getCurrentTimeForTimezone, getCurrentDateForTimezone, convertTo24Hour },
    renderMethods: { getWeatherConditionIcon, getCountryFlag, setUrlParam, getWeatherLottieAsset }
  } = WEATHER_METHODS

  const history = useHistory()
  const location = useLocation()
  const fullPath = location.pathname
  const pathSegments = fullPath.split('/')
  const desiredPath = pathSegments[2] || ''
  const isUserLoggedIn = getLocalLoggedInStatus()
  const { realtimeSubscriptionData } = useContext(CoffeeWebContext)
  const { checkForUserAccess } = commonMethods

  if (userDetails?.id && ROUTE_STRINGS.weather !== fullPath) {
    history.replace({ pathname: ROUTE_STRINGS.weather })
  }

  let menuLock

  if (userDetails && userDetails.businessGeneralMenus?.businessMenu) {
    const menu = userDetails.businessGeneralMenus?.businessMenu?.find(({ menuUrl }) => menuUrl?.includes(desiredPath))

    if (menu) {
      menuLock = menu.menulock
    }
  }
  const subType = userDetails?.sub?.subType
  const isAuthenticatedUser = Boolean(userDetails?.id || isUserLoggedIn)
  const realtimeSubscriptionName = (realtimeSubscriptionData?.subscriptionDetails?.SubscriptionName || '').trim()
  const hasRealtimeSubscription = Boolean(realtimeSubscriptionName)
  const isPlatinumUser = hasRealtimeSubscription ? realtimeSubscriptionName === 'Platinum' || realtimeSubscriptionName === 'Trial Platinum' : subType === 3
  const isMenuLock = (userDetails?.id && !isPlatinumUser) || menuLock

  const uniqueStatesDataRef = useRef([])
  const uniqueRegionsDataRef = useRef([])
  const userSavedLocationsRef = useRef([])
  const toast = useRef(null)
  const params = new URLSearchParams(location.search)
  const type = params.get('type')

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCountry, setSelectedCountry] = useState(null)
  const [selectedState, setSelectedState] = useState(null)
  const [selectedRegion, setSelectedRegion] = useState(null)
  const [isOtherOptionsOpen, setIsOtherOptionsOpen] = useState(false)

  const [uniqueCountries, setUniqueCountries] = useState([])
  const [uniqueStates, setUniqueStates] = useState([])
  const [uniqueRegions, setUniqueRegions] = useState([])
  const [weatherData, setWeatherData] = useState([])
  const [fortNightForecast, setFortNightForecast] = useState([])
  const [hourlyForecast, setHourlyForecast] = useState([])
  const [userSavedLocations, setUserSavedLocations] = useState([])

  const [isLoadingLocations, setIsLoadingLocations] = useState(true)
  const [isLoadingForecast, setIsLoadingForecast] = useState(true)
  const [isLoadingHourly, setIsLoadingHourly] = useState(true)
  const [isSearching, setIsSearching] = useState(false)

  const [currentDay, setCurrentDay] = useState('Today')
  const [currentCity, setCurrentCity] = useState('')
  const [currentForecast, setCurrentForecast] = useState('')
  const [theme, setTheme] = useState(getLocalTheme() || 'light')
  const [otherLocationsData, setOtherLocationsData] = useState(userSavedLocationsRef.current)
  const [savedLocationsFound, setSavedLocationsFound] = useState(true)

  const temperatureData = {
    labels: hourlyForecast.map((item) => getHourlyTime(item.forecast_time)),
    datasets: [
      {
        label: 'Temperature',
        data: hourlyForecast.map((item) => item.temperature),
        borderColor: '#ff9500',
        backgroundColor: 'rgba(255, 149, 0, 0.3)',
        tension: 0.4,
        fill: true
      }
    ]
  }

  const chartOptions = {
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: { size: 14 },
        bodyFont: { size: 12 },
        padding: 12,
        displayColors: false,
        callbacks: {
          label(context) {
            return `${context.parsed.y}°C`
          }
        }
      },
      crosshair: {
        line: {
          color: 'rgba(255, 255, 255, 0.6)',
          width: 1,
          dashPattern: [6, 6]
        },
        zoom: { enabled: false }
      },
      horizontalCrosshair: {
        line: {
          color: 'rgba(255, 255, 255, 0.6)',
          width: 1,
          dashPattern: [6, 6]
        }
      }
    },
    scales: {
      x: {
        ticks: {
          color: '#fff',
          font: { size: 12 }
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      },
      y: {
        position: 'right',
        ticks: {
          color: '#fff',
          font: { size: 12 },
          callback(value) {
            return `${value}°C`
          }
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
          drawBorder: false
        }
      }
    },
    elements: {
      point: { hoverRadius: 8 }
    }
  }

  useEffect(() => {
    const updateTheme = () => {
      const theme = getLocalTheme()

      setTheme(theme)
    }

    window.addEventListener('themeChanged', updateTheme)

    return () => {
      window.removeEventListener('themeChanged', updateTheme)
    }
  }, [])

  useEffect(() => {
    if (!type) {
      const upadatedParam = setUrlParam(location.search, 'coffee-weather')

      history.push({ search: upadatedParam.toString() })

      return
    }
    if (type === 'coffee-weather') {
      setIsOtherOptionsOpen(false)
      fetchUniqueCountriesFunction()

      return
    }
    if (type === 'other-location') {
      setIsOtherOptionsOpen(true)
      setSearchQuery('')
      fetchUserSavedLocationsFunction()
    }
  }, [location.search])

  const fetchUserSavedLocationsFunction = async () => {
    setIsLoadingLocations(true)
    setIsLoadingForecast(true)
    setIsLoadingHourly(true)

    try {
      const { data, error } = await fetchUserSavedLocations(userDetails?.id)

      if (error) {
        console.error('Error fetching user saved locations:', error)

        return
      }

      if (data?.length) {
        const savedWeatherData = await fetchWeatherDataByCountryFunction(0, data, null, null, true)
        const updatedSavedWeatherData = savedWeatherData.map((item) => ({ ...item, isUserSaved: true }))

        userSavedLocationsRef.current = updatedSavedWeatherData
        setUserSavedLocations(updatedSavedWeatherData)
        setOtherLocationsData(updatedSavedWeatherData)
        setSavedLocationsFound(true)
        setCurrentCity(updatedSavedWeatherData[0]?.location_name)
        fetchFortNightWeatherDataFunction(updatedSavedWeatherData[0]?.location_id || updatedSavedWeatherData[0]?.id)
      } else {
        setSavedLocationsFound(false)
        setOtherLocationsData([])
        setCurrentCity('')
        setHourlyForecast([])
        setFortNightForecast([])
        setIsLoadingLocations(false)
        setIsLoadingForecast(false)
        setIsLoadingHourly(false)
      }
    } catch (error) {
      console.error('Error in fetchUserSavedLocationsFunction:', error)
      setIsLoadingLocations(false)
      setIsLoadingForecast(false)
      setIsLoadingHourly(false)
    } finally {
      setIsLoadingLocations(false)
    }
  }

  const fetchUniqueCountriesFunction = async () => {
    setIsLoadingLocations(true)
    setIsLoadingForecast(true)
    setIsLoadingHourly(true)

    try {
      const response = await fetchUniqueCountriesData()

      const uniqueCountriesData = response?.data?.map((item) => ({
        ...item,
        flagURL: getCountryFlag(item?.country_code)
      }))

      if (!uniqueCountriesData?.length) {
        setUniqueCountries([])
        setUniqueStates([])
        setUniqueRegions([])
        setSelectedCountry(null)
        setSelectedState(null)
        setSelectedRegion(null)

        return
      }

      const userCountry = uniqueCountriesData.filter((el) => el?.country_id === userDetails?.countryId)
      const userSelectedCountry = userCountry?.length > 0 ? userCountry : uniqueCountriesData
      const country = userSelectedCountry[0]

      setSelectedCountry(country)
      setUniqueCountries(uniqueCountriesData)

      const uniqueStateResponse = await fetchUniqueStatesData(country?.country_id)

      const uniqueStatesData = uniqueStateResponse?.data?.map((item) => ({
        ...item,
        country_id: country?.country_id,
        state: item.state_name
      }))

      uniqueStatesDataRef.current = uniqueStatesData

      const defaultState = uniqueStatesData[0] ? { state: uniqueStatesData[0].state } : null

      setSelectedState(defaultState)

      let uniqueRegionsData = []
      let defaultRegion = null

      if (defaultState?.state) {
        const uniqueRegionsResponse = await fetchUniqueRegionsData(country?.country_id, uniqueStatesData[0].state_name || defaultState.state)

        uniqueRegionsData = uniqueRegionsResponse?.data.map((item) => ({
          ...item,
          country_id: country?.country_id,
          state: uniqueStatesData[0]?.state_name,
          region: item.region_name
        }))

        uniqueRegionsDataRef.current = uniqueRegionsData

        if (uniqueRegionsData[0]) {
          defaultRegion = { region: uniqueRegionsData[0].region }
        }
      }

      setSelectedRegion(defaultRegion)

      setUniqueStates(uniqueStatesData.map((item) => ({ state: item.state })))
      setUniqueRegions(uniqueRegionsData.map((item) => ({ region: item.region })))

      // Fetch weather data with default state & region
      fetchWeatherDataByCountryFunction(country, [], defaultState?.state, defaultRegion?.region)
    } catch (error) {
      console.error('Error in fetchUniqueCountriesFunction:', error)
      setIsLoadingLocations(false)
      setIsLoadingForecast(false)
      setIsLoadingHourly(false)
    }
  }

  const fetchWeatherDataByCountry = async (countryId, userSearchLocations, state = null, region = null) => {
    let locations = []

    if (userSearchLocations?.length) {
      locations = userSearchLocations
    } else if (countryId) {
      const rpcData = await fetchCountryStateRegionLocations(countryId, state, region)

      locations = rpcData.data || []
    }

    if (!locations?.length) return []

    const locationIds = locations.map((loc) => loc?.location_id || loc.id).filter(Boolean)

    let userLocationMap = {}

    const userDetails = getLocalUserDetails()

    if (locationIds.length > 0 && userDetails?.id) {
      const { data: userLocations } = await fetchOtherLocations(userDetails?.id, locationIds)

      userLocationMap = (userLocations || []).reduce((acc, item) => {
        acc[item.location_id] = true

        return acc
      }, {})
    }

    let locationDetailsMap = {}

    if (locationIds.length > 0) {
      const { data: locationData } = await fetchLocations(locationIds)

      locationDetailsMap = (locationData || []).reduce((acc, loc) => {
        acc[loc.id] = loc

        return acc
      }, {})
    }

    const weatherData = await Promise.all(
      locations.map(async (location) => {
        const locationId = location?.location_id || location.id

        const { data } = await fetchWeatherData(location)

        return {
          ...location,
          ...locationDetailsMap[locationId],
          isInUserLocation: !!userLocationMap[locationId],
          weather: data
        }
      })
    )

    return weatherData
  }

  const fetchWeatherDataByCountryFunction = async (countryData, userSearchLocations = [], state = null, region = null, isSavedLocation = false) => {
    try {
      setIsLoadingLocations(true)
      setIsLoadingForecast(true)
      setIsLoadingHourly(true)

      const weatherDataResponse = await fetchWeatherDataByCountry(countryData?.country_id, userSearchLocations, state, region)

      setWeatherData(weatherDataResponse)
      setCurrentCity(weatherDataResponse[0]?.location_name)

      if (weatherDataResponse?.length) {
        if (!isSavedLocation) {
          const fortNightlyLocationId = userSearchLocations?.length > 0 ? weatherDataResponse[0]?.id : weatherDataResponse[0]?.location_id

          fetchFortNightWeatherDataFunction(fortNightlyLocationId)
        }
      }

      return weatherDataResponse
    } catch (error) {
      console.error('Error fetching weather data by country:', error)
      setIsLoadingForecast(false)
      setIsLoadingHourly(false)
    } finally {
      setIsLoadingLocations(false)
    }
  }

  const fetchFortNightWeatherDataFunction = async (locationId) => {
    setIsLoadingForecast(true)
    setIsLoadingHourly(true)

    try {
      const fortNightResponse = await fetchFortNightWeatherData(locationId)

      const updatedFortNightResponse = fortNightResponse?.data?.map((el) => ({ ...el, location_id: locationId }))

      setFortNightForecast(updatedFortNightResponse || [])

      if (updatedFortNightResponse.length > 0) {
        setCurrentForecast(updatedFortNightResponse[0]?.forecast_date)
        fetchHourlyDataFunction(locationId, updatedFortNightResponse[0].forecast_date)
      } else {
        // No forecast data for this location – clear any previously shown hourly data
        setCurrentForecast('')
        setCurrentDay('Today')
        setHourlyForecast([])
        setIsLoadingHourly(false)
      }
    } catch (error) {
      console.error('Error fetching fort night weather data:', error)
      setIsLoadingHourly(false)
    } finally {
      setIsLoadingForecast(false)
    }
  }

  const fetchHourlyDataFunction = async (locationId, forecastDate) => {
    setIsLoadingHourly(true)
    try {
      const hourlyResponse = await fetchHourlyWeatherData(locationId, forecastDate)

      setCurrentDay(getDayOfWeek(forecastDate))
      setHourlyForecast(hourlyResponse?.data || [])
    } catch (error) {
      console.error('Error fetching hourly weather data:', error)
    } finally {
      setIsLoadingHourly(false)
    }
  }

  const handleFortNightForecastClick = (locationId, forecastDate) => {
    if (currentForecast === forecastDate) return
    setCurrentForecast(forecastDate)
    fetchHourlyDataFunction(locationId, forecastDate)
  }

  const getWeatherIcon = (iconType) => {
    const iconClass =
      {
        sun: 'pi pi-sun',
        cloud: 'pi pi-cloud',
        'sun-cloud': 'pi pi-cloud-sun',
        'cloud-rain': 'pi pi-cloud-rain',
        'cloud-moon': 'pi pi-moon'
      }[iconType] || 'pi pi-circle'

    return <i className={iconClass} />
  }

  const handleLocationClick = useCallback(
    (locationId) => {
      let clickedLocationName = ''

      if (isOtherOptionsOpen) {
        clickedLocationName = otherLocationsData.find((item) => item?.location_id === locationId || item?.id === locationId)?.location_name
      } else {
        clickedLocationName = weatherData.find((item) => item?.location_id === locationId || item?.id === locationId)?.location_name
      }
      if (currentCity === clickedLocationName) return
      setCurrentCity(clickedLocationName)
      fetchFortNightWeatherDataFunction(locationId)
    },
    [currentCity, isOtherOptionsOpen, otherLocationsData, weatherData]
  )

  const handleOtherOption = () => {
    const optionValue = !isOtherOptionsOpen

    setIsLoadingLocations(true)
    setIsLoadingForecast(true)
    setIsLoadingHourly(true)

    setOtherLocationsData([])
    setWeatherData([])
    setFortNightForecast([])
    setHourlyForecast([])
    setCurrentForecast('')
    setCurrentDay('Today')
    setSearchQuery('')

    setIsOtherOptionsOpen(optionValue)

    if (userDetails?.id) {
      const value = optionValue ? 'other-location' : 'coffee-weather'
      const updatedValue = setUrlParam(location.search, value)

      history.push({ search: updatedValue.toString() })
    } else {
      history.push(ROUTE_STRINGS.login)
      setIsOtherOptionsOpen(false)
    }
  }

  const handleDropdownChange = async (e, type) => {
    // Set loading states FIRST to show skeletons instead of old data
    setIsLoadingLocations(true)
    setIsLoadingForecast(true)
    setIsLoadingHourly(true)

    if (type === 'country') {
      const selectedCountryValue = e.target.value

      setSelectedCountry(selectedCountryValue)
      setIsOtherOptionsOpen(false)

      if (isOtherOptionsOpen) {
        const updated = setUrlParam(location.search, 'coffee-weather')

        history.push({ search: updated.toString() })
      }
      // Clear previous data after setting loading states
      setWeatherData([])
      setFortNightForecast([])
      setHourlyForecast([])
      setCurrentForecast('')
      setCurrentDay('Today')

      const statesResponse = await fetchUniqueStatesData(selectedCountryValue?.country_id)

      const statesData = statesResponse?.data?.map((item) => ({
        ...item,
        country_id: selectedCountryValue?.country_id,
        state: item.state_name
      }))

      uniqueStatesDataRef.current = statesData

      setUniqueStates(statesData.map((item) => ({ state: item.state })))

      const defaultState = statesData[0] ? { state: statesData[0].state } : null

      setSelectedState(defaultState)

      let regionsData = []
      let defaultRegion = null

      if (defaultState?.state) {
        const regionRespose = await fetchUniqueRegionsData(selectedCountryValue?.country_id, statesData[0].state_name || defaultState.state)

        regionsData = regionRespose?.data.map((item) => ({
          ...item,
          country_id: selectedCountryValue?.country_id,
          state: statesData[0]?.stateName,
          region: item.region_name
        }))

        uniqueRegionsDataRef.current = regionsData

        setUniqueRegions(regionsData.map((item) => ({ region: item.region })))
        if (regionsData[0]) {
          defaultRegion = { region: regionsData[0].region }
        }
      } else {
        setUniqueRegions([])
      }

      setSelectedRegion(defaultRegion)

      fetchWeatherDataByCountryFunction(selectedCountryValue, [], defaultState?.state, defaultRegion?.region)
    } else if (type === 'state') {
      const newState = e.target.value

      setSelectedState(newState)

      setWeatherData([])
      setFortNightForecast([])
      setHourlyForecast([])
      setCurrentForecast('')
      setCurrentDay('Today')

      const regionResponce = await fetchUniqueRegionsData(selectedCountry?.country_id, newState?.state)

      const regionsData = regionResponce.data.map((item) => ({
        ...item,
        country_iD: selectedCountry?.country_id,
        state: newState?.state,
        region: item?.region_name
      }))

      uniqueRegionsDataRef.current = regionsData

      const regionsForDropdown = regionsData.map((item) => ({ region: item.region }))

      setUniqueRegions(regionsForDropdown)

      const defaultRegion = regionsForDropdown[0] || null

      setSelectedRegion(defaultRegion)

      fetchWeatherDataByCountryFunction(selectedCountry, [], newState?.state, defaultRegion?.region)
    } else if (type === 'region') {
      setSelectedRegion(e.target.value)

      setIsLoadingLocations(true)
      setIsLoadingForecast(true)
      setIsLoadingHourly(true)
      setWeatherData([])
      setFortNightForecast([])
      setHourlyForecast([])
      setCurrentForecast('')
      setCurrentDay('Today')

      fetchWeatherDataByCountryFunction(selectedCountry, [], selectedState?.state, e.target.value?.region)
    }
  }

  const handleCountrySearch = useCallback((e) => {
    const value = e.target.value

    if (value === '') {
      // cancel any pending debounced search so stale calls don't run
      if (debouncedSearch?.cancel) debouncedSearch.cancel()

      setSearchQuery('')
      setOtherLocationsData(userSavedLocationsRef.current)
      setSavedLocationsFound(userSavedLocationsRef.current?.length > 0)

      if (userSavedLocationsRef.current?.length > 0) {
        const firstLocation = userSavedLocationsRef.current[0]

        setCurrentCity(firstLocation?.location_name)
        fetchFortNightWeatherDataFunction(firstLocation?.location_id || firstLocation?.id)
      } else {
        setCurrentCity('')
        setFortNightForecast([])
        setHourlyForecast([])
        setCurrentForecast('')
        setCurrentDay('Today')
      }

      return
    }

    setSearchQuery(value)
  }, [])

  const debounce = (func, delay) => {
    let timeoutId

    const debouncedFunc = (...args) => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => func(...args), delay)
    }

    debouncedFunc.cancel = () => {
      clearTimeout(timeoutId)
    }

    return debouncedFunc
  }

  const debouncedSearch = useRef(
    debounce(async (value) => {
      if (!value || value.trim() === '') {
        setOtherLocationsData(userSavedLocationsRef.current)
        setSearchQuery('')

        setSavedLocationsFound(userSavedLocationsRef.current?.length > 0)

        return
      }

      try {
        setIsSearching(true)
        const userLocationsResponse = await fetchSearchResults(value)

        if (!userLocationsResponse?.data || userLocationsResponse.data.length === 0) {
          setOtherLocationsData([])
          setSavedLocationsFound(false)
          setCurrentCity('')
          setFortNightForecast([])
          setHourlyForecast([])
          setIsSearching(false)

          return
        }

        const locationWeatherData = await fetchWeatherDataByCountryFunction(selectedCountry, userLocationsResponse.data)
        const currentSavedLocations = userSavedLocationsRef.current
        const savedLocationNames = new Set(currentSavedLocations.map((loc) => loc.location_name.toLowerCase().trim()))

        const updatedLocationWeatherData = locationWeatherData?.map((item) => (savedLocationNames.has(item.location_name.toLowerCase()) ? { ...item, isUserSaved: true } : { ...item, location_id: item.id }))

        setOtherLocationsData(updatedLocationWeatherData)
        setSavedLocationsFound(updatedLocationWeatherData?.length > 0)
      } catch (error) {
        console.error('Error searching locations:', error)
        setOtherLocationsData(userSavedLocationsRef.current)
      } finally {
        setIsSearching(false)
      }
    }, 300)
  ).current

  useEffect(() => {
    if (searchQuery === '') {
      if (debouncedSearch?.cancel) debouncedSearch.cancel()

      setOtherLocationsData(userSavedLocationsRef.current)
      setIsSearching(false)
      setSavedLocationsFound(userSavedLocationsRef.current?.length > 0)

      return
    }

    debouncedSearch(searchQuery)
  }, [searchQuery, debouncedSearch])

  const handleAddLocation = useCallback(
    async (e, location, isInOtherLocation) => {
      e.stopPropagation()

      try {
        const updatedLocation = { ...location, isUserSaved: true }

        setUserSavedLocations((prev) => [updatedLocation, ...prev])
        userSavedLocationsRef.current = [updatedLocation, ...userSavedLocationsRef.current]
        setOtherLocationsData([updatedLocation, ...userSavedLocations])

        if (isInOtherLocation) {
          setCurrentCity(updatedLocation?.location_name)
          fetchFortNightWeatherDataFunction(updatedLocation?.location_id)
        }

        setSearchQuery('')
        await saveLocationForUser(userDetails.id, location)

        toast.current?.show({
          severity: 'success',
          detail: `${location.location_name} ${t('HAS_BEEN_ADDED_TO_YOUR_LOCATIONS') || 'has been added to your locations'}`,
          life: 4000
        })
      } catch (error) {
        console.error('Error saving location:', error)
        toast.current?.show({
          severity: 'error',
          detail: t('FAILED_TO_ADD_LOCATION') || 'Failed to add location. Please try again.',
          life: 4000
        })
      }
    },
    [userDetails?.id, userSavedLocations, t]
  )

  const handleRemoveLocation = useCallback(
    async (userId, locationId, locationName, flag, e) => {
      e.stopPropagation()

      try {
        await deleteLocationForUser(userId, locationId)
        setUserSavedLocations((prev) => prev.filter((item) => item.location_id !== locationId))
        userSavedLocationsRef.current = userSavedLocationsRef.current.filter((item) => item.location_id !== locationId)

        const updatedOtherLocationsData = otherLocationsData.filter((item) => item.location_id !== locationId)
        const hasSavedLocations = userSavedLocationsRef.current?.length > 0

        if (!hasSavedLocations) {
          setSavedLocationsFound(hasSavedLocations)
          setOtherLocationsData(userSavedLocationsRef.current)
          if (flag) {
            setCurrentCity('')
            setFortNightForecast([])
            setHourlyForecast([])
            setCurrentForecast('')
          }
          setCurrentDay('Today')
        } else {
          if (flag) {
            setCurrentCity(updatedOtherLocationsData[0]?.location_name)
            fetchFortNightWeatherDataFunction(updatedOtherLocationsData[0]?.location_id)
          }
          setOtherLocationsData(updatedOtherLocationsData)
        }

        toast.current?.show({
          severity: 'success',
          detail: `${locationName} ${t('HAS_BEEN_REMOVED_FROM_YOUR_LOCATIONS') || 'has been removed from your locations'}`,
          life: 4000
        })
      } catch (error) {
        console.error('Error removing location:', error)
        toast.current?.show({
          severity: 'error',
          detail: t('FAILED_TO_REMOVE_LOCATION') || 'Failed to remove location. Please try again.',
          life: 4000
        })
      }
    },
    [otherLocationsData, t]
  )

  const weatherTypeData = {
    fortnightly: {
      header: `${t('DAYS_FORECAST')}`,
      body: ` ${t('WEATHER_FORECAST_DATA_IS_NOT_AVAILABLE_FOR')}`,
      classNameContainer: 'forecast-header',
      classNameButton: 'forecast-header-button'
    },
    hourly: {
      header: `${t('HOURLY_FORECAST')}`,
      body: `${t('HOURLY_FORECAST_DATA_IS_NOT_AVAILABLE_FOR')}`,
      classNameContainer: 'today-forecast-btn-container',
      classNameButton: 'today-forecast-btn'
    }
  }

  const getActiveLocation = () => {
    const locationsSource = isOtherOptionsOpen ? otherLocationsData : weatherData

    if (!locationsSource?.length || !currentCity) return null

    return locationsSource.find((el) => el?.location_name === currentCity) || null
  }

  const getCurrentTemperature = () => {
    const activeLocation = getActiveLocation()

    return activeLocation?.weather?.temperature
  }

  const getHourlyForecast = ({ forecast_time: forecastTime, temperature }) => {
    const formatedTime = getHourlyTime(forecastTime)
    const forecastHour = convertTo24Hour(formatedTime)

    const activeLocation = getActiveLocation()
    const timezone = activeLocation?.timezone
    const weatherCondition = activeLocation?.weather?.weather_condition
    const locationDateTime = activeLocation?.timezone ? new Date(new Date().toLocaleString('en-US', { timeZone: activeLocation.timezone })) : new Date()

    let isNow = false

    if (timezone) {
      const currentTimeInLocation = getCurrentTimeForTimezone(timezone)
      const currentHourInLocation = convertTo24Hour(currentTimeInLocation)

      isNow = currentHourInLocation === forecastHour
    }

    let timeStamp = formatedTime
    let currentTemp = temperature

    if (isNow && currentDay === 'Today') {
      timeStamp = 'Now'
      const latestTemp = getCurrentTemperature()

      if (latestTemp !== undefined && latestTemp !== null) {
        currentTemp = latestTemp
      }
    }

    return { timeStamp, currentTemp, weatherCondition, locationDateTime }
  }

  const { buttonText, infoMessage, className, navigationPath, state } = getAccessButtonAndInfo({ realtimeSubscriptionData })

  const handleExpoTabClick = (wantOtherLocations) => {
    if (wantOtherLocations === isOtherOptionsOpen) return
    handleOtherOption()
  }

  return (
    <div className="weather-screen">
      <div className="details-header-container">
        <div className={`weather-header-tabs ${isOtherOptionsOpen ? 'other-location' : 'coffee-weather'}`}>
          <div className="weather-tabs-indicator" />

          <button type="button" className={`weather-tab-button ${!isOtherOptionsOpen ? 'active' : ''}`} onClick={() => handleExpoTabClick(false)}>
            {t('COFFEE_WEATHER')}
          </button>

          <button type="button" className={`weather-tab-button ${isOtherOptionsOpen ? 'active' : ''}`} onClick={() => handleExpoTabClick(true)}>
            {t('OTHER_LOCATIONS')}
          </button>
        </div>

        <div className="details-header">
          <CustomDropdown disabled={isLoadingLocations} value={selectedCountry} onChange={(e) => handleDropdownChange(e, 'country')} options={uniqueCountries} optionLabel="country_name" showFlags={true} filter={true} placeholder="Select Country" className="weather-country-dropdown" panelClassName="weather-country-dropdown-panel" />
          <CustomDropdown disabled={isOtherOptionsOpen || isLoadingLocations} value={selectedState} onChange={(e) => handleDropdownChange(e, 'state')} options={uniqueStates} optionLabel="state" filter={true} placeholder="Select State" className="weather-country-dropdown" />
          <CustomDropdown disabled={isOtherOptionsOpen || isLoadingLocations} value={selectedRegion} onChange={(e) => handleDropdownChange(e, 'region')} options={uniqueRegions} optionLabel="region" filter={true} placeholder="Select Region" className="weather-country-dropdown" />
        </div>
      </div>
      <div className="weather-content-container">
        <div className="weather-main-container" style={checkForUserAccess(isMenuLock)}>
          <WeatherLocationsColumn isLoadingLocations={isLoadingLocations} isSearching={isSearching} isOtherOptionsOpen={isOtherOptionsOpen} selectedCountry={selectedCountry} selectedState={selectedState} selectedRegion={selectedRegion} weatherData={weatherData} setWeatherData={setWeatherData} currentCity={currentCity} otherLocationsData={otherLocationsData} savedLocationsFound={savedLocationsFound} searchQuery={searchQuery} userDetails={userDetails} onLocationClick={handleLocationClick} onCountrySearch={handleCountrySearch} onAddLocation={handleAddLocation} onRemoveLocation={handleRemoveLocation} getCountryFlag={getCountryFlag} getCurrentDateForTimezone={getCurrentDateForTimezone} getCurrentTimeForTimezone={getCurrentTimeForTimezone} getWeatherConditionIcon={getWeatherConditionIcon} getWeatherLottieAsset={getWeatherLottieAsset} />

          <div className="weather-forecast-column">
            <div className="forecast-list">
              {isLoadingForecast ? (
                <ForecastItemSkeleton />
              ) : (
                <>
                  {fortNightForecast.length > 0 ? (
                    <>
                      <div className="forecast-header">
                        <button className="forecast-header-button">{t('_10_DAYS_FORECAST')}</button>
                      </div>
                      {fortNightForecast.map((forecast, index) => (
                        <div key={index} className={`forecast-item ${currentForecast === forecast?.forecast_date ? 'active' : ''} ${theme === 'light' && currentForecast === forecast?.forecast_date ? 'light-theme' : 'dark-theme'}`} onClick={() => handleFortNightForecastClick(forecast?.location_id, forecast?.forecast_date)}>
                          <div className="forecast-day">
                            <div>{currentForecast === forecast?.forecast_date && <input type="radio" name="forecast" value={forecast?.forecast_date} defaultChecked />}</div>
                            {getDayOfWeek(forecast?.forecast_date)}
                          </div>

                          <div className="forecast-icon-wrapper">
                            {(() => {
                              const icon = getWeatherConditionIcon(forecast?.weather_condition, false)

                              return typeof icon === 'string' ? <img src={icon} alt={forecast?.weather_condition || 'Weather'} className="weather-animation" /> : <Lottie animationData={icon} loop={true} className="weather-animation" />
                            })()}
                            <div className="rain-chance-badge">{forecast?.precipitation_probability}%</div>
                          </div>
                          <div className="forecast-temp-low">{Number(forecast?.low_temperature).toFixed(0)}°C</div>
                          <div className="forecast-temp-bar">
                            <div
                              className="forecast-temp-bar-fill"
                              style={{
                                width: forecast?.high_temperature != null && forecast?.low_temperature != null ? `${((forecast.high_temperature - forecast.low_temperature) / 20) * 100}%` : '0%'
                              }}
                            />
                          </div>
                          <div className="forecast-temp-high">{Number(forecast?.high_temperature).toFixed(0)}°C</div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <>{isOtherOptionsOpen ? <OtherLocationsNoDataCard duration={'FortNight'} searchQuery={searchQuery} /> : <WeatherNoDataCard weatherTypeData={weatherTypeData} weatherType="fortnightly" currentCity={currentCity} type={type} getWeatherConditionIcon={getWeatherConditionIcon} />}</>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="weather-details-column">
            {isLoadingHourly ? (
              <>
                <HourlyForecastSkeleton />
              </>
            ) : (
              <>
                {hourlyForecast.length > 0 ? (
                  <>
                    <div className="today-forecast-btn-container">
                      <button className="today-forecast-btn">{currentDay}</button>
                    </div>
                    <div className="hourly-forecast-section">
                      {hourlyForecast.map((row, rowIndex) => {
                        const forecast = getHourlyForecast(row)
                        const isNow = forecast?.timeStamp === 'Now'
                        const condition = isNow ? forecast?.weatherCondition : row.weather_condition
                        const dateTime = isNow ? forecast.locationDateTime : new Date(row.forecast_time)

                        const icon = getWeatherConditionIcon(condition, !isNow, dateTime)

                        return (
                          <div key={rowIndex} className="hour-row">
                            <div className={`hourly-forecast-item ${isNow ? 'now-frame' : ''}`}>
                              <div className={`hourly-time ${isNow ? 'now' : ''}`}>{forecast?.timeStamp}</div>
                              {typeof icon === 'string' ? <img src={icon} alt={condition || 'Weather'} className="weather-animation-small" /> : <Lottie animationData={icon} loop className="weather-animation-small" />}
                              <div className="hourly-temp">{Number(forecast?.currentTemp).toFixed(0)}°C</div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </>
                ) : (
                  <>{isOtherOptionsOpen ? <OtherLocationsNoDataCard duration={'Hourly'} searchQuery={searchQuery} /> : <WeatherNoDataCard weatherTypeData={weatherTypeData} weatherType="hourly" currentCity={currentCity} type={type} getWeatherConditionIcon={getWeatherConditionIcon} />}</>
                )}
              </>
            )}

            {isLoadingHourly ? (
              <GraphSkeleton />
            ) : (
              <>
                {hourlyForecast.length > 0 && (
                  <div className="temperature-graph-container">
                    <div className="graph-icons-row">
                      <div className="graph-icon">{getWeatherIcon('cloud-moon')}</div>
                      <div className="graph-icon">{getWeatherIcon('sun')}</div>
                      <div className="graph-icon">{getWeatherIcon('cloud')}</div>
                      <div className="graph-icon">{getWeatherIcon('cloud')}</div>
                    </div>
                    <div className="graph-wrapper">
                      <Chart type="line" data={temperatureData} options={chartOptions} />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <Toast ref={toast} position="top-right" className="weather-toast" />

      {(!isAuthenticatedUser || isMenuLock) && (
        <div className={`weather-create-account-info-modal-popup ${isUserLoggedIn ? 'create-account-info-message' : 'guest-create-account-info-message'}`} data-testid="coffee-reports-create-account-container">
          <CreateAccountInfoModal buttonText={buttonText} infoMessage={infoMessage} className={className} navigationPath={navigationPath} state={state} testId="coffee-reports-create-account-modal" />
        </div>
      )}
    </div>
  )
}

export default Weather
